import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowRight, Brain, Globe, TestTube, Plane, TrendingUp, Unlock, Eye } from 'lucide-react';
import { mockData } from '../../mock/data';
import { useTranslation } from '../../hooks/useTranslation';

const UseCasesSection = () => {
  const { useCases } = mockData;
  const { t } = useTranslation();
  
  // Get translated use cases using the same method as UseCasePage
  const getTranslatedUseCase = (useCase) => ({
    ...useCase,
    title: t(`useCasePage.useCases.${useCase.slug}.title`),
    description: t(`useCasePage.useCases.${useCase.slug}.description`)
  });

  const iconMap = {
    Brain: Brain,
    Globe: Globe,
    TestTube: TestTube,
    Plane: Plane,
    TrendingUp: TrendingUp,
    Unlock: Unlock,
    Eye: Eye
  };

  return (
    <section className="py-24 bg-gradient-to-b from-slate-950 to-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
            {t('useCases.title1')} <span className="text-cyan-400">{t('useCases.title2')}</span>
          </h2>
          <p className="text-xl text-slate-400 max-w-3xl mx-auto">
            {t('useCases.subtitle')}
          </p>
        </div>

        {/* Use Cases Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {useCases.slice(0,6).map((useCase, index) => {
            const IconComponent = iconMap[useCase.icon] || Globe;
            const translatedUseCase = getTranslatedUseCase(useCase);
            
            return (
              <Link
                key={index}
                to={`/use-cases/${useCase.slug}`}
                className="group block bg-slate-900/30 backdrop-blur-sm border border-slate-800 rounded-2xl p-6 hover:border-cyan-500/50 hover:bg-slate-900/50 transition-all duration-300 hover:scale-105"
              >
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-xl mb-4 group-hover:scale-110 transition-transform duration-300">
                  <IconComponent className="w-7 h-7 text-cyan-400" />
                </div>
                
                <h3 className="text-xl font-bold text-white mb-4 group-hover:text-cyan-400 transition-colors duration-300">
                  {translatedUseCase.title}
                </h3>
                
                <p className="text-slate-400 leading-relaxed mb-4">
                  {translatedUseCase.description}
                </p>
                
                <div className="flex items-center text-cyan-400 text-sm font-medium group-hover:text-cyan-300">
                  {t('common.learnMore')}
                  <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform duration-200" />
                </div>
              </Link>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center">
          <Link to="/use-cases">
            <Button 
              size="lg"
              className="h-11 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold px-10 py-5 text-lg shadow-xl"
            >
              {t('useCases.exploreAll')}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default UseCasesSection;